import { SearchFieldEnum, useGlobalContext, usePrevious, useRenderCount, AnywhereSearchResultEnum, getRecentSearch, parseBoolean, } from '@honeycomb/data';
import { Autocompleter, AutocompleterCategory, AutocompleterField, AutocompleterList, AutocompleterOption, AutocompleterResults, HotelIcon, ButtonField, LocationIcon, Styler, useBreakpoints, Chip, FlexGrid, Button, ChevronDownIcon, ChevronUpIcon, Notification, InformationIcon, PopoverAnchor, useHoneycombTheme, Anchor, } from '@honeycomb/ui-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { SearchDisplayType, useSearchContext } from '../SearchContext';
import { SearchDispatchActionType } from '../SearchReducer';
import { AutocompleterSkeleton } from '../components/AutocompleterSkeleton';
import { FieldErrorMessage } from '../components/FieldErrorMessage';
import { FieldDialog } from '../components/FieldDialog';
import { AutocompleterNoMatches } from '../components/AutocompleterNoMatches';
import { pluraliseCount } from '../../../utils/pluraliseCount';
import { SearchFieldPopover } from '../components/SearchFieldPopover/SearchFieldPopover';
import { SearchFieldPopoverPanel } from '../components/SearchFieldPopover/SearchFieldPopoverPanel';
import { SEARCH_FIELD_POPOVER_INNER_PADDING, SEARCH_FIELD_TRANSITION } from '../../../utils/style-constants';
const anywhereOptions = [
    {
        id: Number(AnywhereSearchResultEnum.ANYWHERE_ID),
        title: `${AnywhereSearchResultEnum.TITLE}`,
        type: Number(AnywhereSearchResultEnum.TYPE),
        hotelCount: 0,
        isCached: true,
    },
];
const getCompositeValue = (destination) => {
    return `${destination.id}-${destination.type}`;
};
// Define the function to build the list of options for a given set of destinations/hotels
const buildOptionsList = (options, labelledById, removeable = false) => {
    return (React.createElement(AutocompleterList, { labelledById: labelledById }, options.map((option) => {
        const { title, subTitle: subtitle, isCached } = option;
        const compositeValue = getCompositeValue(option);
        return (React.createElement(AutocompleterOption, { key: `option-${compositeValue}`, id: compositeValue, value: compositeValue, title: title, subtitle: subtitle !== null && subtitle !== void 0 ? subtitle : '', icon: option.type === 1 ? React.createElement(HotelIcon, null) : React.createElement(LocationIcon, null), action: (() => {
                if (removeable)
                    return 'remove';
                if (isCached)
                    return 'checkbox';
                return 'single-select';
            })() }));
    })));
};
const autocompleterFieldId = 'destination-autocompleter-field';
const autocompleterListId = 'destination-autocompleter-list';
const dialogAutocompleterFieldId = `${autocompleterFieldId}-dialog`;
export function getDestinationSummary(searchState) {
    var _a, _b;
    if ((_a = searchState.destinations) === null || _a === void 0 ? void 0 : _a.length) {
        const title = (_b = searchState.destinations[0]) === null || _b === void 0 ? void 0 : _b.title;
        const count = searchState.destinations.length;
        const checkCount = count > 2 ? 's' : '';
        const setTitle = `${title} (+ ${count - 1} other${checkCount})`;
        return searchState.hotelTitle || (count > 1 ? setTitle : title) || '';
    }
    return '';
}
const POPOVER_WIDTH = 740;
export function MultiSearchDestinationAutocompleter({ state, actions, }) {
    var _a, _b, _c, _d;
    const [showAllRecentOptions, setShowAllRecentOptions] = useState(false);
    const { largeUp } = useBreakpoints();
    const recentSearches = getRecentSearch();
    const recentOptions = recentSearches && recentSearches.length > 0 ? JSON.parse(recentSearches) : [];
    const { data, previousData, loading, searchClicked, error: errorProp, popupPlacement = 'bottom-start', called, } = state;
    const { fetchData, onDone, setSearchClicked } = actions;
    const { openField, onOpen, onClose, displayType, searchDispatch, searchState, fieldValidity, searchError } = useSearchContext();
    const { resourceStrings: { HC_TOP_SELLERS, HC_RECENT_SEARCHES, HC_DESTINATIONS, SEARCH_DESTINATION_ERROR_EMPTY, HC_HOTELS, HC_CLEAR_ALL, SEARCH_DESTINATION_FIELD_LABEL, SEARCH_DESTINATION_FIELD_PLACEHOLDER, RESULT_COUNT_MESSAGE, RESULTS_COUNT_MESSAGE, NONCACHED_FIELD_ERROR_MESSAGE, SEARCH_ALREADYSELECTED_CATEGORY_TITLE, }, configSettings: { HCEnableDestinationGroups }, } = useGlobalContext();
    // T O D O: Destination groups are potentially redunant now as they can be returned by the autocompleter.
    // We could still use this for "popular" groups.
    const destinationGroups = parseBoolean(HCEnableDestinationGroups)
        ? (_b = (_a = data === null || data === void 0 ? void 0 : data.destinationAutocompleterResults) === null || _a === void 0 ? void 0 : _a.destinationGroups) !== null && _b !== void 0 ? _b : []
        : [];
    // DEV ONLY: Count the number of renders of the component
    useRenderCount('DestinationAutoCompleter');
    const selectedDestinations = (searchState.destinations || []).filter(Boolean);
    const selectedValues = React.useMemo(() => {
        var _a;
        return (_a = selectedDestinations.map((d) => `${d === null || d === void 0 ? void 0 : d.id}-${d === null || d === void 0 ? void 0 : d.type}`)) !== null && _a !== void 0 ? _a : [];
    }, [selectedDestinations]);
    const [selectionError, setSelectionError] = React.useState(false);
    const [updateId, setUpdateId] = React.useState('');
    const [searchTerm, setSearchTerm] = React.useState('');
    // Is the search currently open on this field
    const open = openField === SearchFieldEnum.DESTINATION;
    const prevOpen = usePrevious(open);
    const error = (_d = (_c = fieldValidity[SearchFieldEnum.DESTINATION]) === null || _c === void 0 ? void 0 : _c.error) !== null && _d !== void 0 ? _d : false;
    const withSearchTerm = searchTerm !== '';
    const withSelections = selectedValues.length > 0;
    // Get the data and store the previous instance to smoothly switch when it changes
    const prevUpdateId = usePrevious(updateId);
    const popup = displayType === SearchDisplayType.POPUP;
    const dialog = displayType === SearchDisplayType.DIALOG;
    const [scrollingPopoverElement, setScrollingPopoverElement] = React.useState(null);
    const [previouslySelectedDestinations, setPreviouslySelectedDestinations] = useState([]);
    const inputRef = React.useRef(null);
    React.useEffect(() => {
        // On open, we want to display any previously selected destinations in our "Already selected" category.
        // We won't add new selections as this will causes a layout shift for the user. However, we will
        // remove any selections which are removed.
        if (open && !prevOpen) {
            setPreviouslySelectedDestinations(selectedDestinations);
        }
    }, [open, prevOpen, selectedDestinations]);
    // Extract the destination and hotel data to be displayed
    const destinationOptions = useMemo(() => {
        var _a, _b;
        return loading && previousData
            ? (_a = previousData === null || previousData === void 0 ? void 0 : previousData.destinationAutocompleterResults.destinations) !== null && _a !== void 0 ? _a : []
            : (_b = data === null || data === void 0 ? void 0 : data.destinationAutocompleterResults.destinations) !== null && _b !== void 0 ? _b : [];
    }, [loading, previousData, data]);
    const destinationGroupOptions = useMemo(() => {
        var _a, _b;
        return loading && previousData
            ? (_a = previousData === null || previousData === void 0 ? void 0 : previousData.destinationAutocompleterResults.destinationGroups) !== null && _a !== void 0 ? _a : []
            : (_b = data === null || data === void 0 ? void 0 : data.destinationAutocompleterResults.destinationGroups) !== null && _b !== void 0 ? _b : [];
    }, [loading, previousData, data]);
    const hotelOptions = useMemo(() => {
        var _a, _b;
        return loading && previousData
            ? (_a = previousData === null || previousData === void 0 ? void 0 : previousData.destinationAutocompleterResults.hotels) !== null && _a !== void 0 ? _a : []
            : (_b = data === null || data === void 0 ? void 0 : data.destinationAutocompleterResults.hotels) !== null && _b !== void 0 ? _b : [];
    }, [loading, previousData, data]);
    const matchesFound = destinationOptions.length > 0 || hotelOptions.length > 0;
    const summary = useMemo(() => {
        return getDestinationSummary(searchState);
    }, [searchState]);
    useEffect(() => {
        if (prevOpen && !open) {
            if (!searchState.destinationId && !searchState.hotelId) {
                searchDispatch({ type: SearchDispatchActionType.SET_DESTINATION_TITLE });
                searchDispatch({ type: SearchDispatchActionType.SET_HOTEL_TITLE });
            }
        }
    }, [prevOpen, open, searchDispatch, searchState.destinationId, searchState.hotelId]);
    useEffect(() => {
        if (updateId && updateId !== prevUpdateId) {
            onDone === null || onDone === void 0 ? void 0 : onDone();
            onClose();
        }
    }, [onClose, onDone, prevUpdateId, updateId]);
    // Define the functions for the autocompleter to work
    const handleFieldChange = useCallback((event) => {
        const { value } = event.currentTarget;
        setSearchTerm(value);
        setSearchClicked(false);
        setSelectionError(false);
    }, [setSearchClicked]);
    const handleClearSearchTerm = () => {
        setSearchTerm('');
    };
    const handleRemove = useCallback((value) => {
        searchDispatch({
            type: SearchDispatchActionType.REMOVE_FLEXIBLE_DESTINATION,
            idAndType: value,
        });
        setSelectionError(false);
        // Also remove from our "Already selected" category.
        setPreviouslySelectedDestinations((destinations) => {
            const [id, type] = value.split('-');
            return destinations.filter((dest) => !(String(dest.id) === id && String(dest.type) === type));
        });
        handleClearSearchTerm();
    }, [searchDispatch]);
    const handleRemoveChip = useCallback((value) => (_event) => {
        var _a;
        handleRemove(value);
        // Focus the input after a chip interaction
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [handleRemove]);
    const handleFieldKeyDown = useCallback((event) => {
        if (event.key === 'Backspace') {
            // If there is no text in the field then backspace should delete the last chip.
            if (searchTerm === '') {
                const last = selectedDestinations[selectedDestinations.length - 1];
                if (last) {
                    handleRemove(getCompositeValue(last));
                }
            }
        }
    }, [handleRemove, searchTerm, selectedDestinations]);
    const handleClear = () => {
        var _a;
        setSelectionError(false);
        searchDispatch({
            type: SearchDispatchActionType.CLEAR_FLEXIBLE_DESTINATION,
        });
        // Also clear our "Already selected" category
        setPreviouslySelectedDestinations([]);
        // Focus the input after clearing
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const handleAdd = (value) => {
        var _a, _b;
        const [id, type] = value.split('-');
        if (!id || !type) {
            return;
        }
        // Find the destination object associated with this value
        const allOptions = [
            ...destinationOptions,
            ...hotelOptions,
            ...destinationGroupOptions,
            ...anywhereOptions,
            ...recentOptions,
        ];
        const destinationObject = allOptions.find((option) => String(option.id) === id && String(option.type) === type);
        if (!destinationObject) {
            return;
        }
        const isCached = (_a = destinationObject.isCached) !== null && _a !== void 0 ? _a : false;
        const isAnywhere = destinationObject.id === AnywhereSearchResultEnum.ANYWHERE_ID;
        const isSingleSelect = isAnywhere || !isCached;
        const anywhereAlreadySelected = selectedDestinations.some((d) => (d === null || d === void 0 ? void 0 : d.id) === AnywhereSearchResultEnum.ANYWHERE_ID);
        const cachedAlreadySelected = !anywhereAlreadySelected && selectedDestinations.some((d) => d === null || d === void 0 ? void 0 : d.isCached);
        if (!isCached && cachedAlreadySelected) {
            // We cannot mix cached and non-cached selections. Display a message to the user.
            setSelectionError(true);
            return;
        }
        searchDispatch({
            type: SearchDispatchActionType.ADD_FLEXIBLE_DESTINATION,
            destination: {
                id: destinationObject.id,
                type: destinationObject.type,
                title: destinationObject.title,
                isCached: (_b = destinationObject.isCached) !== null && _b !== void 0 ? _b : false,
                subTitle: destinationObject.subTitle,
            },
        });
        if (isSingleSelect) {
            // The user has either selected 'Anywhere' or a non-cached destination.
            // These are single-select options so we want to close the popup/dialog.
            onClose();
            setUpdateId(uuid());
        }
        if (isCached) {
            // Clear the selection error message.
            setSelectionError(false);
        }
        handleClearSearchTerm();
    };
    const handlePopupClose = () => {
        if (selectedValues.length) {
            setUpdateId(uuid());
        }
        onClose();
        setSelectionError(false);
    };
    const destinationCategoryTitle = withSearchTerm ? HC_DESTINATIONS : HC_TOP_SELLERS;
    const getRecentCategory = () => {
        if (recentOptions.length === 0) {
            return null;
        }
        const maxItems = !largeUp ? 3 : null;
        const visibleItems = maxItems && !showAllRecentOptions ? recentOptions.slice(0, maxItems) : recentOptions;
        const hiddenCount = showAllRecentOptions ? 0 : recentOptions.length - (maxItems || 0);
        const button = maxItems && recentOptions.length > maxItems ? (React.createElement(Styler, { display: "flex", justifyContent: "center" },
            React.createElement(Button, { color: "neutral", size: "xs", icon: !showAllRecentOptions ? React.createElement(ChevronDownIcon, { size: "xs" }) : React.createElement(ChevronUpIcon, { size: "xs" }), onClick: () => setShowAllRecentOptions((show) => !show), iconPosition: "end" }, !showAllRecentOptions ? `Show ${hiddenCount} more` : 'Show less'))) : undefined;
        return (React.createElement(AutocompleterCategory, { title: HC_RECENT_SEARCHES, appendix: pluraliseCount(recentOptions.length, {
                singular: RESULT_COUNT_MESSAGE,
                plural: RESULTS_COUNT_MESSAGE,
            }), footerContent: button, id: "category-recent", key: "category-recent" }, buildOptionsList(visibleItems, 'category-recent')));
    };
    const destinationsCategory = React.useMemo(() => {
        return destinationOptions.length || hotelOptions.length ? (React.createElement(AutocompleterCategory, { title: destinationCategoryTitle, appendix: pluraliseCount(destinationOptions.length, {
                singular: RESULT_COUNT_MESSAGE,
                plural: RESULTS_COUNT_MESSAGE,
            }), id: "category-destinations", key: "category-destinations" }, destinationOptions.length ? buildOptionsList(destinationOptions, 'category-destinations') : undefined)) : null;
    }, [
        RESULTS_COUNT_MESSAGE,
        RESULT_COUNT_MESSAGE,
        destinationCategoryTitle,
        destinationOptions,
        hotelOptions.length,
    ]);
    const destinationsCombinedCategory = React.useMemo(() => {
        const destinationGroupOptionsComb = destinationGroupOptions;
        const destinationOptionsComb = destinationOptions;
        return destinationOptions.length || hotelOptions.length ? (React.createElement(AutocompleterCategory, { title: destinationCategoryTitle, appendix: pluraliseCount(destinationOptions.length, {
                singular: RESULT_COUNT_MESSAGE,
                plural: RESULTS_COUNT_MESSAGE,
            }), id: "category-combined-destinations", key: "category-combined-destinations" }, destinationOptions.length
            ? buildOptionsList(destinationGroupOptionsComb.concat(destinationOptionsComb), 'category-combined-destinations')
            : undefined)) : null;
    }, [
        RESULTS_COUNT_MESSAGE,
        RESULT_COUNT_MESSAGE,
        destinationGroupOptions,
        destinationCategoryTitle,
        destinationOptions,
        hotelOptions.length,
    ]);
    const hotelsCategory = React.useMemo(() => {
        return destinationOptions.length || hotelOptions.length ? (React.createElement(AutocompleterCategory, { title: HC_HOTELS, appendix: pluraliseCount(hotelOptions.length, {
                singular: RESULT_COUNT_MESSAGE,
                plural: RESULTS_COUNT_MESSAGE,
            }), id: "category-hotels", key: "category-hotels" }, hotelOptions.length ? buildOptionsList(hotelOptions, 'category-hotels') : undefined)) : null;
    }, [HC_HOTELS, RESULTS_COUNT_MESSAGE, RESULT_COUNT_MESSAGE, hotelOptions, destinationOptions.length]);
    const getFirstColumnCategories = () => {
        const categories = [];
        // Previous selections
        if (previouslySelectedDestinations.length > 0) {
            categories.push(React.createElement(AutocompleterCategory, { title: SEARCH_ALREADYSELECTED_CATEGORY_TITLE, endDecorator: React.createElement(Anchor, { button: true, onClick: handleClear, size: "xs", weight: "semiBold", truncate: true, "data-id": "destination-autocomp-clear" }, HC_CLEAR_ALL), id: "category-selections", key: "category-selections" }, buildOptionsList(previouslySelectedDestinations, 'category-selections', true)));
        }
        // If we have a search term then we want to display matching destinations.
        // If there are no matching destinations then display hotels instead.
        if (withSearchTerm) {
            if (destinationsCombinedCategory) {
                categories.push(destinationsCombinedCategory);
            }
            else if (hotelsCategory) {
                categories.push(hotelsCategory);
            }
            return categories;
        }
        // If we don't have a search term then we display `Anywhere` and `Recent searches`.
        // Destination Group
        const destinationGroup = (React.createElement(AutocompleterCategory, { id: "category-groups", key: "category-groups" },
            React.createElement(AutocompleterList, null, anywhereOptions
                .map((d) => {
                const { type, title, subTitle, isCached } = d;
                const compositeValue = getCompositeValue(d);
                return (React.createElement(AutocompleterOption, { key: `option-${compositeValue}`, id: compositeValue, value: compositeValue, title: title, subtitle: subTitle !== null && subTitle !== void 0 ? subTitle : '', icon: type === 1 ? React.createElement(HotelIcon, null) : React.createElement(LocationIcon, null), action: "single-select", "data-id": `multi-autocomp-option-${isCached ? 'checkbox' : 'link'}` }));
            })
                .concat(Object.entries(destinationGroups).map(([_key, group]) => {
                const { title, subTitle } = group;
                const compositeValue = getCompositeValue(group);
                return (React.createElement(AutocompleterOption, { key: `option-${compositeValue}`, id: compositeValue, value: compositeValue, title: title, subtitle: subTitle !== null && subTitle !== void 0 ? subTitle : '', icon: React.createElement(LocationIcon, null), action: "checkbox", "data-id": "multi-autocomp-option-link" }));
            })))));
        categories.push(destinationGroup);
        const recentCategory = getRecentCategory();
        if (recentCategory) {
            categories.push(recentCategory);
        }
        return categories;
    };
    const getSecondColumnCategories = () => {
        const result = [];
        // If we have a search term then we want to display matching hotels
        // (but only if they were not displayed in the first column).
        if (withSearchTerm) {
            if (destinationsCategory && hotelsCategory) {
                result.push(hotelsCategory);
            }
            return result;
        }
        // If we don't have a search term then we display default destinations.
        if (destinationsCategory) {
            result.push(destinationsCategory);
        }
        return result;
    };
    const getSecondColumnStartIndex = () => {
        // Get the start index for the second column of results. This is required for keyboard navigation
        // to work across the two AutcompleterResults components.
        if (withSearchTerm) {
            const totalOptionsInFirstColumn = destinationGroupOptions.length + destinationOptions.length + previouslySelectedDestinations.length;
            return totalOptionsInFirstColumn + 1;
        }
        return (recentOptions.length +
            anywhereOptions.length +
            destinationGroups.length +
            previouslySelectedDestinations.length +
            1);
    };
    const getChips = useCallback(() => {
        return selectedDestinations.map((item) => (React.createElement(Chip, { size: "2xs", color: "primary", cornerStyle: "rounded", onDismiss: handleRemoveChip(getCompositeValue(item)), tabIndex: -1, key: item.id, "data-id": "selected-destination-chip" }, item === null || item === void 0 ? void 0 : item.title)));
    }, [handleRemoveChip, selectedDestinations]);
    // Build the final results element including "no matches" case for empty searches or apollo errors
    const getAutocompleterResults = () => {
        // Results loading
        if (!called || loading) {
            return (React.createElement(FlexGrid, { container: true, spacing: SEARCH_FIELD_POPOVER_INNER_PADDING, disableEqualOverflow: true },
                React.createElement(FlexGrid, { xs: 12, l: 6 },
                    React.createElement(AutocompleterSkeleton, { multiSelect: true })),
                React.createElement(FlexGrid, { xs: 12, l: 6 },
                    React.createElement(AutocompleterSkeleton, { multiSelect: true }))));
        }
        // No matches
        if (!matchesFound) {
            return React.createElement(AutocompleterNoMatches, null);
        }
        return (React.createElement(FlexGrid, { container: true, spacing: SEARCH_FIELD_POPOVER_INNER_PADDING, disableEqualOverflow: true },
            React.createElement(FlexGrid, { xs: 12, l: 6 },
                React.createElement(AutocompleterResults, { id: autocompleterListId, label: "Suggested Destinations", spacing: 3 }, getFirstColumnCategories())),
            React.createElement(FlexGrid, { xs: 12, l: 6 },
                React.createElement(AutocompleterResults, { id: autocompleterListId, label: "Suggested Destinations", startIndex: getSecondColumnStartIndex() }, getSecondColumnCategories()))));
    };
    const fieldError = (React.createElement(FieldErrorMessage, { error: error, message: SEARCH_DESTINATION_ERROR_EMPTY, "data-id": "destination-error-message" }));
    const selectionErrorNotification = selectionError ? (React.createElement(Notification, { icon: React.createElement(InformationIcon, null), color: "info", variant: "compact", mb: 2 }, NONCACHED_FIELD_ERROR_MESSAGE)) : null;
    const label = SEARCH_DESTINATION_FIELD_LABEL;
    const placeholder = selectedDestinations.length === 0 ? SEARCH_DESTINATION_FIELD_PLACEHOLDER : 'Add more';
    const handleOpen = useCallback(() => {
        onOpen(SearchFieldEnum.DESTINATION);
        handleClearSearchTerm();
    }, [onOpen]);
    const handleClickOrFocus = () => {
        if (!open) {
            handleOpen();
        }
    };
    const commonFieldProps = useMemo(() => ({
        validationStatus: error ? 'error' : undefined,
        variant: 'filled',
    }), [error]);
    const { theme } = useHoneycombTheme();
    const innerPadding = SEARCH_FIELD_POPOVER_INNER_PADDING * theme.spacingUnit;
    return (React.createElement(React.Fragment, null,
        React.createElement(Autocompleter, { fetch: fetchData, onAdd: handleAdd, onRemove: handleRemove, selectedValue: selectedValues, multiSelect: true, scrollContext: scrollingPopoverElement || undefined },
            React.createElement(React.Fragment, null,
                popup && open && (React.createElement(SearchFieldPopover, { open: open, onClose: () => handlePopupClose(), placement: popupPlacement, error: searchError, dynamicHeight: true, variant: "wrapping-field" },
                    React.createElement(PopoverAnchor, null,
                        React.createElement(AutocompleterField, Object.assign({ inputRef: inputRef, id: autocompleterFieldId, listId: autocompleterListId, value: error && searchClicked ? '' : searchTerm, label: label, placeholder: placeholder, onChange: handleFieldChange, onKeyDown: handleFieldKeyDown, autoFocus: true, shrinkLabel: true, size: { xs: 'm', m: 'l' }, showClear: withSearchTerm, onButtonClick: handleClearSearchTerm, chips: getChips(), FieldRootProps: Object.assign({ pos: 'relative', transition: SEARCH_FIELD_TRANSITION }, (open && {
                                zIndex: theme.zIndex.popup + 1,
                                w: POPOVER_WIDTH - innerPadding * 2,
                            })), fullWidth: !open }, commonFieldProps))),
                    React.createElement(SearchFieldPopoverPanel, { width: POPOVER_WIDTH, scrollingElementRef: setScrollingPopoverElement },
                        selectionErrorNotification,
                        getAutocompleterResults()))),
                !open && (
                // A button acts as a trigger to open the popup or dialog.
                React.createElement(ButtonField, Object.assign({ value: summary, label: label, onClick: handleClickOrFocus, onFocus: () => handleClickOrFocus(), size: { xs: 'm', m: 'l' }, fullWidth: true }, commonFieldProps))),
                fieldError),
            dialog && (React.createElement(FieldDialog, { open: open, onClose: onClose, title: label, headingProps: {
                    component: 'label',
                    htmlFor: dialogAutocompleterFieldId,
                }, ctaProps: {
                    onClick: () => {
                        if (onDone) {
                            onDone();
                        }
                    },
                    label: 'Next',
                    disabled: !withSelections,
                }, headerContent: React.createElement(React.Fragment, null,
                    React.createElement(AutocompleterField, Object.assign({ inputRef: inputRef, id: dialogAutocompleterFieldId, listId: autocompleterListId, value: searchTerm, placeholder: placeholder, onChange: handleFieldChange, onKeyDown: handleFieldKeyDown, autoFocus: true, showClear: withSearchTerm, onButtonClick: handleClearSearchTerm, fullWidth: true, chips: getChips() }, commonFieldProps)),
                    fieldError) },
                selectionErrorNotification,
                getAutocompleterResults()))),
        errorProp && React.createElement("div", null, errorProp.message)));
}
